import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, videoCategories } from '@/mocks/videos';
import type { CategoryItem } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = videoCategories.find((c) => c.slug === slug) as CategoryItem | undefined;

  const categoryVideos = slug
    ? videos.filter((v) => v.category === slug)
    : videos;

  const otherCategories = videoCategories.filter((c) => c.slug !== slug);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Category Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
            {currentCategory ? (
              <span className="flex items-center gap-2">
                <i className={`${currentCategory.icon} text-primary-500`}></i>
                {currentCategory.name}
              </span>
            ) : (
              '全部分类'
            )}
          </h1>
          <p className="text-sm text-foreground-500">
            {currentCategory
              ? `共找到 ${categoryVideos.length} 部${currentCategory.name}相关视频`
              : `共找到 ${videos.length} 部视频`}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="mb-6 md:mb-8 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 min-w-max">
            {videoCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/80'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Video Grid */}
        {categoryVideos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
            {categoryVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-movie-2-line text-3xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 text-sm">该分类暂无视频，请查看其他分类</p>
          </div>
        )}

        {/* Other Categories Quick Links */}
        <section className="mt-10 md:mt-14 pt-8 border-t border-background-200/70" aria-label="其他分类">
          <h3 className="text-base md:text-lg font-heading font-bold text-foreground-900 mb-4">
            探索更多分类
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherCategories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-700 text-sm transition-all duration-200 whitespace-nowrap"
              >
                <i className={`${cat.icon}`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: currentCategory ? `${currentCategory.name} - 密桃视频` : '全部分类 - 密桃视频',
        description: currentCategory
          ? `密桃视频${currentCategory.name}分类，聚合红桃视频、樱桃视频等热门站高清${currentCategory.name}内容，免费在线观看不卡顿。`
          : '密桃视频全部分类，海量高清电影、电视剧、综艺、动漫免费在线观看。',
        url: `https://www.mitaovideo.com/category/${slug || ''}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '密桃视频',
          url: 'https://www.mitaovideo.com/',
        },
      }) }} />
    </div>
  );
}